import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { adminOrdersApi } from '../api';
import { usePlatform } from '../hooks/usePlatform';
import { useAppAlert } from '../contexts/AlertContext';
import { Loading } from '../components/Loading';
import { getNextStatus, canCancelOrder, getNextStatusLabel, getStatusLabel } from '../utils/orderStatus';
import type { Order, PaymentMethod } from '../types';
import './AdminOrderDetailPage.css';

const PAYMENT_METHODS: { value: PaymentMethod; label: string }[] = [
  { value: 'cash', label: 'Наличные' },
  { value: 'card', label: 'Карта' },
  { value: 'transfer', label: 'Перевод' },
];

export const AdminOrderDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const platform = usePlatform();
  const { showAlert, showConfirm } = useAppAlert();

  const [order, setOrder] = useState<Order | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [statusUpdating, setStatusUpdating] = useState(false);

  // Payment method modal
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [newPaymentMethod, setNewPaymentMethod] = useState<PaymentMethod | ''>('');
  const [paymentUpdating, setPaymentUpdating] = useState(false);

   
  useEffect(() => {
    platform.showBackButton(() => navigate('/admin/orders'));

    return () => {
      platform.hideBackButton();
    };
    // platform identity is stable; navigate is referentially stable in react-router v6
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    loadOrder();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const loadOrder = async () => {
    if (!id) return;

    try {
      setLoading(true);
      setError(null);
      const response = await adminOrdersApi.getOrder(Number(id));
      setOrder(response);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Ошибка загрузки заказа');
    } finally {
      setLoading(false);
    }
  };

  const handleMoveToNextStatus = async () => {
    if (!order) return;

    const nextStatus = getNextStatus(order.status);
    if (!nextStatus) return;

    const nextStatusLabel = getNextStatusLabel(order.status);

    showConfirm(`Перевести заказ в статус "${nextStatusLabel}"?`, async (confirmed) => {
      if (!confirmed) return;

      try {
        setStatusUpdating(true);
        await adminOrdersApi.updateStatus(order.id, {
          status: nextStatus,
        });

        // Reload order to get updated data
        await loadOrder();

        showAlert('Статус заказа обновлен');
      } catch (err: unknown) {
        showAlert(err instanceof Error ? err.message : 'Ошибка обновления статуса');
      } finally {
        setStatusUpdating(false);
      }
    });
  };

  const handleCancelOrder = async () => {
    if (!order) return;

    if (!canCancelOrder(order.status)) {
      showAlert('Заказ нельзя отменить на данном этапе');
      return;
    }

    showConfirm('Вы уверены, что хотите отменить заказ?', async (confirmed) => {
      if (!confirmed) return;

      try {
        setStatusUpdating(true);
        await adminOrdersApi.updateStatus(order.id, {
          status: 'cancelled',
        });

        // Reload order to get updated data
        await loadOrder();

        showAlert('Заказ отменен');
      } catch (err: unknown) {
        showAlert(err instanceof Error ? err.message : 'Ошибка отмены заказа');
      } finally {
        setStatusUpdating(false);
      }
    });
  };

  const handleUpdatePayment = async () => {
    if (!order || !newPaymentMethod) return;

    try {
      setPaymentUpdating(true);
      await adminOrdersApi.updatePayment(order.id, {
        payment_method: newPaymentMethod,
      });

      // Reload order to get updated data
      await loadOrder();

      setShowPaymentModal(false);
      setNewPaymentMethod('');

      showAlert('Метод оплаты обновлен');
    } catch (err: unknown) {
      showAlert(err instanceof Error ? err.message : 'Ошибка обновления метода оплаты');
    } finally {
      setPaymentUpdating(false);
    }
  };

  const formatPrice = (price: number) => {
    return `${price.toFixed(0)} ₽`;
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString('ru-RU', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const getPaymentMethodLabel = (method: string) => {
    return PAYMENT_METHODS.find(m => m.value === method)?.label || method;
  };

  const getStatusColor = (status: string) => {
    const colors: Record<string, string> = {
      pending: 'status-pending',
      paid: 'status-paid',
      preparing: 'status-preparing',
      delivering: 'status-delivering',
      completed: 'status-completed',
      cancelled: 'status-cancelled',
    };
    return colors[status] || '';
  };

  if (loading) {
    return <Loading />;
  }

  if (error || !order) {
    return (
      <div className="admin-order-detail-error">
        <div className="error-icon">❌</div>
        <h3>Ошибка</h3>
        <p>{error || 'Заказ не найден'}</p>
        <button className="retry-button" onClick={loadOrder}>
          Попробовать снова
        </button>
      </div>
    );
  }

  return (
    <div className="admin-order-detail-page">
      <div className="page-header">
        <h1>Заказ #{order.id}</h1>
        <span className={`order-status-badge ${getStatusColor(order.status)}`}>
          {getStatusLabel(order.status)}
        </span>
      </div>

      {/* Order Info */}
      <div className="detail-section">
        <h2>Информация о заказе</h2>
        <div className="info-grid">
          <div className="info-item">
            <span className="info-label">Дата создания:</span>
            <span className="info-value">{formatDate(order.created_at)}</span>
          </div>
          <div className="info-item">
            <span className="info-label">Последнее обновление:</span>
            <span className="info-value">{formatDate(order.updated_at)}</span>
          </div>
          <div className="info-item">
            <span className="info-label">Сумма заказа:</span>
            <span className="info-value">{formatPrice(order.total_amount - order.delivery_fee)}</span>
          </div>
          <div className="info-item">
            <span className="info-label">Доставка:</span>
            <span className="info-value">{formatPrice(order.delivery_fee)}</span>
          </div>
          <div className="info-item total">
            <span className="info-label">Итого:</span>
            <span className="info-value">{formatPrice(order.total_amount)}</span>
          </div>
        </div>
      </div>

      {/* Customer Info */}
      {order.customer && (
        <div className="detail-section">
          <h2>Информация о клиенте</h2>
          <div className="info-grid">
            <div className="info-item">
              <span className="info-label">Имя:</span>
              <span className="info-value">
                {order.customer.first_name} {order.customer.last_name || ''}
              </span>
            </div>
            {order.customer.username && (
              <div className="info-item">
                <span className="info-label">Username:</span>
                <span className="info-value">@{order.customer.username}</span>
              </div>
            )}
            {order.customer.phone && (
              <div className="info-item">
                <span className="info-label">Телефон:</span>
                <span className="info-value">{order.customer.phone}</span>
              </div>
            )}
            <div className="info-item">
              <span className="info-label">Платформа:</span>
              <span className="info-value">
                {order.customer.platform === 'max' ? 'MAX' : 'Telegram'}
              </span>
            </div>
            {order.customer.platform === 'telegram' && order.customer.telegram_id !== undefined && (
              <div className="info-item">
                <span className="info-label">Telegram ID:</span>
                <span className="info-value">{order.customer.telegram_id}</span>
              </div>
            )}
            {order.customer.platform === 'max' && order.customer.max_id !== undefined && (
              <div className="info-item">
                <span className="info-label">MAX ID:</span>
                <span className="info-value">{order.customer.max_id}</span>
              </div>
            )}
            <div className="info-item">
              <span className="info-label">Клиент с:</span>
              <span className="info-value">{formatDate(order.customer.created_at)}</span>
            </div>
          </div>
          {/* Contact link is Telegram-specific. MAX has no equivalent deep-link
              for "open chat with user by ID", so we hide the button on MAX. */}
          {order.customer.platform === 'telegram' && (
            <a
              href={order.customer.username
                ? `https://t.me/${order.customer.username}`
                : `tg://user?id=${order.customer.telegram_id}`
              }
              target="_blank"
              rel="noopener noreferrer"
              className="contact-customer-button"
            >
              💬 Связаться с клиентом
            </a>
          )}
        </div>
      )}

      {/* Delivery Address */}
      {order.delivery_address && (
        <div className="detail-section">
          <h2>Адрес доставки</h2>
          <div className="address-box">
            {'address_line' in order.delivery_address && order.delivery_address.address_line && (
              <p>{order.delivery_address.address_line}</p>
            )}
            {'city' in order.delivery_address && order.delivery_address.city && (
              <p>{order.delivery_address.city}</p>
            )}
            {'postal_code' in order.delivery_address && order.delivery_address.postal_code && (
              <p>Индекс: {order.delivery_address.postal_code}</p>
            )}
            {order.delivery_address.notes && (
              <p className="address-notes">Примечание: {order.delivery_address.notes}</p>
            )}
          </div>
        </div>
      )}

      {/* Order Notes */}
      {order.notes && (
        <div className="detail-section">
          <h2>Заметки клиента</h2>
          <div className="notes-box">{order.notes}</div>
        </div>
      )}

      {/* Items */}
      {order.items && order.items.length > 0 && (
        <div className="detail-section">
          <h2>Товары</h2>
          <div className="items-list">
            {order.items.map((item) => (
              <div key={item.id} className="order-item">
                {item.image_url && (
                  <img src={item.image_url} alt={item.product_name} className="item-image" />
                )}
                <div className="item-info">
                  <h3>{item.product_name}</h3>
                  <div className="item-details">
                    <span>{formatPrice(item.price)} × {item.quantity} шт.</span>
                    <span className="item-subtotal">{formatPrice(item.subtotal)}</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Payment Info */}
      <div className="detail-section">
        <div className="section-header">
          <h2>Информация об оплате</h2>
          <button className="action-button" onClick={() => setShowPaymentModal(true)}>
            {order.payment_method ? 'Изменить' : 'Установить'}
          </button>
        </div>
        <div className="info-grid">
          <div className="info-item">
            <span className="info-label">Метод оплаты:</span>
            <span className="info-value">
              {order.payment_method ? getPaymentMethodLabel(order.payment_method) : 'Не указан'}
            </span>
          </div>
          {order.paid_at && (
            <div className="info-item">
              <span className="info-label">Дата оплаты:</span>
              <span className="info-value">{formatDate(order.paid_at)}</span>
            </div>
          )}
        </div>
      </div>

      {/* Status Management */}
      <div className="detail-section">
        <h2>Управление статусом</h2>
        <div className="status-actions">
          {getNextStatus(order.status) && (
            <button
              className="status-action-button primary"
              onClick={handleMoveToNextStatus}
              disabled={statusUpdating}
            >
              {statusUpdating ? 'Обновление...' : `Перевести в "${getNextStatusLabel(order.status)}"`}
            </button>
          )}
          {canCancelOrder(order.status) && (
            <button
              className="status-action-button cancel"
              onClick={handleCancelOrder}
              disabled={statusUpdating}
            >
              {statusUpdating ? 'Отмена...' : 'Отменить заказ'}
            </button>
          )}
          {!getNextStatus(order.status) && !canCancelOrder(order.status) && (
            <p className="status-final-message">
              Заказ находится в финальном статусе
            </p>
          )}
        </div>
      </div>

      {/* Status History */}
      {order.status_history && order.status_history.length > 0 && (
        <div className="detail-section">
          <h2>История статусов</h2>
          <div className="status-history">
            {order.status_history.map((history) => (
              <div key={history.id} className="history-item">
                <div className="history-header">
                  <span className={`history-status ${getStatusColor(history.status)}`}>
                    {getStatusLabel(history.status)}
                  </span>
                  <span className="history-date">{formatDate(history.created_at)}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Payment Method Modal */}
      {showPaymentModal && (
        <div className="modal-overlay" onClick={() => setShowPaymentModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h3>Установить метод оплаты</h3>
            <div className="modal-body">
              <label>
                <span className="modal-label">Метод оплаты:</span>
                <select
                  value={newPaymentMethod}
                  onChange={(e) => setNewPaymentMethod(e.target.value as PaymentMethod)}
                  className="modal-select"
                >
                  <option value="">Выберите метод</option>
                  {PAYMENT_METHODS.map((method) => (
                    <option key={method.value} value={method.value}>
                      {method.label}
                    </option>
                  ))}
                </select>
              </label>
            </div>
            <div className="modal-actions">
              <button
                className="modal-button cancel"
                onClick={() => setShowPaymentModal(false)}
                disabled={paymentUpdating}
              >
                Отмена
              </button>
              <button
                className="modal-button confirm"
                onClick={handleUpdatePayment}
                disabled={!newPaymentMethod || paymentUpdating}
              >
                {paymentUpdating ? 'Обновление...' : 'Обновить'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
